/**
 * PublishabilityDashboard — Slide-over panel that lists all novels scoring
 * 85 %+ and lets the author export them individually or in bulk as a ZIP
 * (cover + manuscript DOCX + KDP metadata CSV + authorship certificate).
 *
 * Usage:
 *   <PublishabilityDashboard open={open} onClose={() => setOpen(false)} />
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Download,
  ExternalLink,
  Loader2,
  Package,
  PackageOpen,
  RefreshCw,
  Trophy,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { publishabilityApi, kdpExportApi } from '@/lib/api';

/* ── Score badge colour ────────────────────────────────────────────── */
function ScoreBadge({ score }) {
  const colour =
    score >= 90
      ? 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-300'
      : 'bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-300';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${colour}`}
      data-testid="pub-dash-score-badge"
    >
      {score}%
    </span>
  );
}

/* ── Cover thumbnail ───────────────────────────────────────────────── */
function CoverThumb({ coverUrl, title, genre }) {
  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-md shadow-sm"
      style={{ width: 44, height: 66 }}
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 p-1 text-center"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          <span className="line-clamp-3 text-[7px] font-semibold leading-tight text-white">
            {title}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Novel row ─────────────────────────────────────────────────────── */
function NovelRow({ novel, onExportSingle }) {
  const [exporting, setExporting] = useState(false);
  const [kdpModalOpen, setKdpModalOpen] = useState(false);

  const handleExport = (e) => {
    e.stopPropagation();
    setKdpModalOpen(true);
  };

  return (
    <>
      <div
        className="group flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 transition hover:border-primary/40 hover:bg-card/80"
        data-testid={`pub-dash-row-${novel.project_id}`}
      >
        <CoverThumb
          coverUrl={novel.cover_url}
          title={novel.title}
          genre={novel.genre}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-[12.5px] font-semibold leading-tight text-foreground">
              {novel.title || 'Untitled'}
            </span>
            <ScoreBadge score={novel.publishability_score} />
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            {novel.genre || 'Novel'} · {novel.chapters_count || '?'} chapters
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Open in editor */}
          <a
            href={`/app/?project=${novel.project_id}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="rounded border border-border p-1.5 text-muted-foreground transition hover:border-primary/60 hover:text-foreground"
            title="Open in editor"
            data-testid={`pub-dash-open-${novel.project_id}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          {/* Per-row KDP export */}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10.5px] font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
            data-testid={`pub-dash-export-${novel.project_id}`}
          >
            {exporting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            Export
          </button>
        </div>
      </div>

      {/* KDP Keywords modal reuse */}
      {kdpModalOpen && (
        <KdpExportModal
          projectId={novel.project_id}
          onClose={() => setKdpModalOpen(false)}
        />
      )}
    </>
  );
}

/* ── KDP export modal (mirrors MyBookshelfCarousel's KdpKeywordsModal) ─ */
function KdpExportModal({ projectId, onClose }) {
  const [keywords, setKeywords] = useState(Array(7).fill(''));
  const [loading, setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    kdpExportApi
      .suggestKeywords(projectId)
      .then((r) => {
        if (!cancelled)
          setKeywords(
            (r.keywords || []).map((k) => k || '').concat(Array(7).fill('')).slice(0, 7),
          );
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const doDownload = async () => {
    setExporting(true);
    try {
      await kdpExportApi.download(projectId, keywords.filter(Boolean));
      toast.success('KDP export downloaded — authorship certificate included.');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="pub-dash-kdp-modal-overlay"
    >
      <div
        className="relative w-[420px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-background p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="pub-dash-kdp-modal"
      >
        <h3 className="text-[14.5px] font-semibold text-foreground">
          KDP Export Keywords
        </h3>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">
          Edit any keyword before downloading.
        </p>
        {loading ? (
          <div className="my-6 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[12px]">Analysing synopsis…</span>
          </div>
        ) : (
          <div className="mt-4 space-y-1.5">
            {keywords.map((kw, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right text-[10.5px] text-muted-foreground">
                  {i + 1}.
                </span>
                <input
                  value={kw}
                  onChange={(e) =>
                    setKeywords((prev) => {
                      const n = [...prev];
                      n[i] = e.target.value;
                      return n;
                    })
                  }
                  placeholder={`Keyword ${i + 1}`}
                  maxLength={50}
                  className="flex-1 rounded border border-border bg-background px-2.5 py-1.5 text-[12px] outline-none focus:border-primary"
                  data-testid={`pub-dash-kw-input-${i}`}
                />
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5 text-[11.5px] text-muted-foreground hover:text-foreground"
            data-testid="pub-dash-kdp-cancel"
          >
            Cancel
          </button>
          <button
            onClick={doDownload}
            disabled={exporting || loading}
            className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3 py-1.5 text-[11.5px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            data-testid="pub-dash-kdp-download"
          >
            {exporting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            {exporting ? 'Building ZIP…' : 'Download ZIP'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main panel ─────────────────────────────────────────────────────── */
export default function PublishabilityDashboard({ open, onClose }) {
  const [novels, setNovels]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await publishabilityApi.list();
      setNovels(res.novels || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load publishable novels.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load when panel opens
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleExportAll = async () => {
    if (novels.length === 0) return;
    setExporting(true);
    try {
      await publishabilityApi.downloadBatch();
      toast.success(
        `Batch export downloaded — ${novels.length} novel${novels.length !== 1 ? 's' : ''} packaged.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[480px] flex-col p-0 sm:max-w-[520px]"
        data-testid="publishability-dashboard-panel"
      >
        {/* Header */}
        <SheetHeader className="border-b border-border bg-panel-header px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600/15">
                <Trophy className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <SheetTitle className="text-[14px] font-semibold leading-tight text-foreground">
                  Publishability Dashboard
                </SheetTitle>
                <p className="text-[10.5px] text-muted-foreground">
                  Novels scoring 85 %+ · ready for KDP
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {/* Refresh */}
              <button
                onClick={load}
                disabled={loading}
                className="rounded-md p-1.5 text-muted-foreground transition hover:bg-secondary hover:text-foreground disabled:opacity-50"
                title="Refresh"
                data-testid="pub-dash-refresh-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {/* Export All */}
              <button
                onClick={handleExportAll}
                disabled={exporting || loading || novels.length === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                data-testid="pub-dash-export-all-btn"
                title={`Export all ${novels.length} publishable novels as one ZIP`}
              >
                {exporting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Package className="h-3 w-3" />
                )}
                {exporting ? 'Packaging…' : `Export All (${novels.length})`}
              </button>
            </div>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="pub-dash-body">
          {loading ? (
            <div className="flex flex-1 items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="text-[12px] text-muted-foreground">
                  Loading publishable novels…
                </span>
              </div>
            </div>
          ) : error ? (
            <div
              className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
              data-testid="pub-dash-error"
            >
              <p className="text-[12px] font-semibold text-destructive">{error}</p>
              <button
                onClick={load}
                className="inline-flex items-center gap-1 rounded border border-destructive/40 px-3 py-1.5 text-[11.5px] text-destructive hover:bg-destructive/10"
              >
                <RefreshCw className="h-3 w-3" /> Retry
              </button>
            </div>
          ) : novels.length === 0 ? (
            <div
              className="flex flex-col items-center gap-4 py-20 text-center"
              data-testid="pub-dash-empty"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
                <PackageOpen className="h-7 w-7 text-muted-foreground" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-foreground">
                  No publishable novels yet
                </p>
                <p className="mt-1 max-w-[260px] text-[11.5px] text-muted-foreground">
                  Novels need a publishability score of 85 %+ to appear here. Run the
                  ZIP import pipeline or Bulk Eval to score your manuscripts.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="pub-dash-novels-list">
              {/* Summary bar */}
              <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 dark:border-emerald-800 dark:bg-emerald-900/20">
                <BookOpen className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  {novels.length} novel{novels.length !== 1 ? 's' : ''} ready for publication
                </span>
              </div>

              {novels.map((novel) => (
                <NovelRow
                  key={novel.project_id}
                  novel={novel}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        {!loading && novels.length > 0 && (
          <div className="border-t border-border bg-panel-header px-4 py-2.5">
            <p className="text-[10px] text-muted-foreground">
              Each export contains: manuscript (.docx) + KDP metadata (.csv) + authorship certificate (.pdf)
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
